import { useEffect, useRef, useState } from "react";
import { X, Send, Mail, AlertCircle, Paperclip, Bold, Italic, Underline, List, ListOrdered, Link2, RemoveFormatting, Save, Trash2 } from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmModal";
import { SIGNATURE_SANITIZE_CONFIG } from "../lib/emailSanitize";

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024; // 20 MB total, matches backend
const AUTOSAVE_INTERVAL_MS = 15_000;

// Small helper — read a File as base64 (strip data URL prefix)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function humanBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Rich-text email compose modal for Pragma.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - defaultTo (str)
 *  - linkedType         "contact" | "lead" | "deal"
 *  - linkedId
 *  - onSent ()
 *  - onDraftChange ()   fired after a draft is saved or deleted so a
 *                       parent list can re-fetch
 *  - replyContext       { to, cc, subject, quoted_html, thread_id, in_reply_to_id }
 *                       When set: title becomes "Reply", Cc/Bcc panel is
 *                       expanded by default, and a quoted-body block is
 *                       inserted below the editor cursor.
 *  - initialDraft       Existing draft to resume. Overrides defaultTo /
 *                       replyContext (the draft carries its own copy).
 */
export default function ComposeEmailModal({ open, onClose, defaultTo = "", linkedType, linkedId, onSent, onDraftChange, replyContext = null, initialDraft = null }) {
  const { user } = useAuth();
  const confirm = useConfirm();

  // Resolve reply context — a resumed draft carries its own copy.
  const effectiveReply = initialDraft?.reply_context || replyContext;
  const isReply = !!effectiveReply;

  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{filename, mime_type, content_b64, size}]
  const [includeSignature, setIncludeSignature] = useState(true);
  const [trackEmail, setTrackEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);

  // ---- Draft state ---------------------------------------------------------
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hadAttachmentsHint, setHadAttachmentsHint] = useState(false);
  const dirtyRef = useRef(false);
  const snapshotRef = useRef(""); // last-saved snapshot for change detection
  const draftIdRef = useRef(null);
  const savingRef = useRef(false);
  const sentRef = useRef(false); // guard: don't autosave after successful send

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const signature = user?.email_signature || "";

  const markDirty = () => { dirtyRef.current = true; };

  // Reset all fields whenever the modal opens (or the resumed draft changes).
  useEffect(() => {
    if (!open) return;
    sentRef.current = false;
    if (initialDraft) {
      setTo(initialDraft.to || "");
      setSubject(initialDraft.subject || "");
      setCc(initialDraft.cc || "");
      setBcc(initialDraft.bcc || "");
      setShowCcBcc(!!(initialDraft.cc || initialDraft.bcc));
      setIncludeSignature(initialDraft.include_signature !== false);
      setTrackEmail(initialDraft.track !== false);
      setDraftId(initialDraft.id);
      draftIdRef.current = initialDraft.id;
      setLastSavedAt(initialDraft.updated_at || null);
      setHadAttachmentsHint(!!initialDraft.had_attachments);
    } else if (isReply) {
      setTo(effectiveReply.to || "");
      setSubject(effectiveReply.subject || "");
      setCc(effectiveReply.cc || "");
      setBcc("");
      setShowCcBcc(true);   // Reply-all needs Cc visible for editing
      setIncludeSignature(true);
      setTrackEmail(true);
      setDraftId(null);
      draftIdRef.current = null;
      setLastSavedAt(null);
      setHadAttachmentsHint(false);
    } else {
      setTo(defaultTo);
      setSubject("");
      setCc("");
      setBcc("");
      setShowCcBcc(false);
      setIncludeSignature(true);
      setTrackEmail(true);
      setDraftId(null);
      draftIdRef.current = null;
      setLastSavedAt(null);
      setHadAttachmentsHint(false);
    }
    setAttachments([]);
    setSending(false);
    dirtyRef.current = false;
    if (editorRef.current) {
      if (initialDraft) {
        editorRef.current.innerHTML = initialDraft.body_html || "";
      } else if (isReply) {
        // Cursor lands on line 1, quoted email sits below (Gmail-style).
        editorRef.current.innerHTML = `<p><br></p><p><br></p>${effectiveReply.quoted_html || ""}`;
      } else {
        editorRef.current.innerHTML = "";
      }
      snapshotRef.current = editorRef.current.innerHTML;
    }
    api.get("/integrations/status")
      .then(({ data }) => setGmailStatus(data.google))
      .catch(() => setGmailStatus({ connected: false, configured: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTo, initialDraft?.id, replyContext]);

  // Build the payload used by both manual + auto save.
  const buildDraftPayload = () => {
    const html = editorRef.current?.innerHTML || "";
    return {
      to: to.trim(),
      cc: cc.trim() || null,
      bcc: bcc.trim() || null,
      subject: subject.trim(),
      body_html: html,
      linked_type: linkedType || null,
      linked_id: linkedId || null,
      include_signature: includeSignature,
      track: trackEmail,
      reply_context: effectiveReply ? {
        thread_id: effectiveReply.thread_id || null,
        in_reply_to_id: effectiveReply.in_reply_to_id || null,
        quoted_html: effectiveReply.quoted_html || null,
        orig_to: effectiveReply.to || null,
        orig_cc: effectiveReply.cc || null,
        orig_subject: effectiveReply.subject || null,
      } : null,
      had_attachments: attachments.length > 0,
    };
  };

  // Save (create or update). silent=true suppresses toast on periodic autosave.
  const doSaveDraft = async ({ silent = false } = {}) => {
    if (savingRef.current || sentRef.current) return;
    const html = editorRef.current?.innerHTML || "";
    const hasContent = to.trim() || subject.trim() || html.replace(/<[^>]*>/g, "").trim();
    if (!hasContent) {
      if (!silent) toast.error("Nothing to save yet");
      return;
    }
    savingRef.current = true;
    setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      let saved;
      if (draftIdRef.current) {
        const { data } = await api.put(`/email-drafts/${draftIdRef.current}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/email-drafts", payload);
        saved = data;
        setDraftId(data.id);
        draftIdRef.current = data.id;
      }
      snapshotRef.current = html;
      dirtyRef.current = false;
      setLastSavedAt(saved.updated_at || new Date().toISOString());
      if (!silent) toast.success("Draft saved");
      onDraftChange?.();
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save draft");
    } finally {
      savingRef.current = false;
      setSavingDraft(false);
    }
  };

  // Auto-save every 15s while dirty. Uses ref-based checks so the closure
  // stays stable and we don't reset the interval on every keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => {
      // Detect body-html changes even if state hasn't ticked.
      const html = editorRef.current?.innerHTML || "";
      if (html !== snapshotRef.current) dirtyRef.current = true;
      if (dirtyRef.current && !savingRef.current && !sentRef.current) {
        doSaveDraft({ silent: true });
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const exec = (cmd, value) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    markDirty();
  };

  const insertLink = async () => {
    const url = await confirm({
      title: "Insert link",
      input: { placeholder: "https://", defaultValue: "https://", required: true, label: "Link URL" },
      confirmLabel: "Insert",
    });
    if (url) exec("createLink", url);
  };

  const totalAttachmentSize = attachments.reduce((n, a) => n + (a.size || 0), 0);

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let running = totalAttachmentSize;
    const next = [...attachments];
    for (const f of files) {
      if (running + f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${f.name} would exceed the 20 MB total attachment limit — skipped.`);
        continue;
      }
      try {
        const b64 = await fileToBase64(f);
        next.push({
          filename: f.name,
          mime_type: f.type || "application/octet-stream",
          content_b64: b64,
          size: f.size,
        });
        running += f.size;
      } catch {
        toast.error(`Could not read file: ${f.name}`);
      }
    }
    setAttachments(next);
    markDirty();
    if (next.length > 0) setHadAttachmentsHint(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments(a => a.filter((_, i) => i !== idx));
    markDirty();
  };

  const deleteDraft = async () => {
    if (!draftIdRef.current) { onClose(); return; }
    const ok = await confirm({
      title: "Discard this draft?",
      message: "Your saved changes will be permanently deleted.",
      destructive: true,
      confirmLabel: "Discard draft",
    });
    if (!ok) return;
    try {
      await api.delete(`/email-drafts/${draftIdRef.current}`);
      sentRef.current = true; // block any lingering autosave
      onDraftChange?.();
      onClose();
    } catch {
      toast.error("Failed to discard draft");
    }
  };

  const handleClose = async () => {
    // Detect changes since last save.
    const html = editorRef.current?.innerHTML || "";
    const changed = dirtyRef.current || html !== snapshotRef.current;
    const hasContent = to.trim() || subject.trim() || html.replace(/<[^>]*>/g, "").trim() || attachments.length > 0;
    if (!changed || !hasContent) {
      onClose();
      return;
    }
    const choice = await confirm({
      title: draftIdRef.current ? "Save changes to draft?" : "Save this draft?",
      message: "You have unsaved changes. Save them so you can pick up right where you left off?",
      confirmLabel: "Save draft",
      neutralLabel: "Discard",
      cancelLabel: "Keep editing",
    });
    // Returns: true=save, false=discard (neutral), null=keep editing (cancel/Esc)
    if (choice === null) return; // keep editing
    if (choice === true) {
      await doSaveDraft({ silent: false });
      onClose();
    } else {
      // Discard: if there was a saved draft, delete it too so state is consistent.
      if (draftIdRef.current) {
        try { await api.delete(`/email-drafts/${draftIdRef.current}`); onDraftChange?.(); } catch { /* ignore */ }
      }
      sentRef.current = true;
      onClose();
    }
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Add a recipient"); return; }
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.innerText || "";
    setSending(true);
    try {
      await api.post("/integrations/google/send-email", {
        to: to.trim(),
        subject: subject.trim(),
        body: text,
        body_html: html,
        cc: cc.trim() || null,
        bcc: bcc.trim() || null,
        linked_type: linkedType || null,
        linked_id: linkedId || null,
        include_signature: includeSignature,
        track: trackEmail,
        attachments: attachments.map(({ size, ...rest }) => rest),
        thread_id: effectiveReply?.thread_id || null,
        in_reply_to_id: effectiveReply?.in_reply_to_id || null,
      });
      // Clean up draft on successful send.
      sentRef.current = true;
      if (draftIdRef.current) {
        try { await api.delete(`/email-drafts/${draftIdRef.current}`); } catch { /* ignore */ }
        onDraftChange?.();
      }
      toast.success(isReply ? "Reply sent" : "Email sent");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
    setSending(false);
  };

  const canSend = gmailStatus?.connected;

  const savedAgo = lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div
      data-testid="compose-email-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "var(--pg-bg)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top toolbar */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 20px",
          borderBottom: "1px solid var(--pg-border)",
          background: "var(--pg-surface)",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          data-testid="compose-email-close"
          className="pg-btn pg-btn-ghost"
          style={{ padding: 6 }}
          aria-label="Close"
        >
          <X strokeWidth={2.2} size={16} />
        </button>
        <div
          className="invite-modal-icon"
          style={{
            background: "rgba(234,67,53,0.10)", color: "#EA4335",
            width: 34, height: 34, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <Mail strokeWidth={2.2} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
            {isReply ? "Reply" : (draftId ? "Draft" : "New email")}
          </div>
          <div className="text-xs" style={{ color: "var(--pg-text-mute)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {gmailStatus?.connected
              ? `${isReply ? "Replying as" : "Sending as"} ${gmailStatus.account?.email}`
              : "Connect Gmail in Settings → Integrations to enable sending"}
            {savedAgo && (
              <span data-testid="compose-email-saved-at" style={{ marginLeft: 8, opacity: 0.85 }}>
                · {savingDraft ? "Saving…" : `Saved ${savedAgo}`}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="pg-btn"
          onClick={() => doSaveDraft({ silent: false })}
          disabled={savingDraft}
          data-testid="compose-email-save-draft"
          style={{ fontSize: 12 }}
          title="Save as draft"
        >
          <Save size={13} /> {savingDraft ? "Saving…" : "Save draft"}
        </button>
        <button
          type="button"
          className="pg-btn pg-btn-primary"
          onClick={send}
          disabled={!canSend || sending || !to.trim()}
          data-testid="compose-email-send-top"
        >
          {sending ? "Sending…" : (<><Send size={13} /> Send</>)}
        </button>
      </div>

      {/* Scroll body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 20px 100px" }}>
        <div
          className="invite-modal"
          style={{
            maxWidth: 780, margin: "0 auto",
            padding: 0, boxShadow: "none", background: "transparent", border: "none",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
        {!canSend && gmailStatus !== null && (
          <div className="invite-modal-error" data-testid="compose-email-not-connected" style={{ marginTop: 0, marginBottom: 16 }}>
            <AlertCircle strokeWidth={2.2} />
            {gmailStatus?.configured
              ? "Gmail isn't connected for your account yet. Head to Settings → Integrations."
              : "Google integration isn't configured on this workspace."}
          </div>
        )}

        {hadAttachmentsHint && (
          <div
            data-testid="compose-email-attach-hint"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", marginBottom: 12,
              borderRadius: 8,
              background: "rgba(255,193,7,0.10)",
              border: "1px solid rgba(255,193,7,0.35)",
              fontSize: 12, color: "var(--pg-text)",
            }}
          >
            <Paperclip size={13} strokeWidth={2.2} />
            <span>This draft had attachments in the previous session — please re-attach any files you still need.</span>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label className="invite-modal-label">To</label>
          {!showCcBcc && (
            <button
              type="button"
              onClick={() => setShowCcBcc(true)}
              data-testid="compose-email-toggle-ccbcc"
              style={{
                background: "none", border: "none", padding: "2px 4px",
                color: "var(--pg-text-mute)", fontSize: 11, cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              + Cc / Bcc
            </button>
          )}
        </div>
        <input
          type="email"
          value={to}
          onChange={(e) => { setTo(e.target.value); markDirty(); }}
          className="invite-modal-input"
          placeholder="recipient@example.com"
          data-testid="compose-email-to"
          required
        />

        {showCcBcc && (
          <>
            <label className="invite-modal-label">Cc</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => { setCc(e.target.value); markDirty(); }}
              className="invite-modal-input"
              placeholder="one@example.com, two@example.com"
              data-testid="compose-email-cc"
            />
            <label className="invite-modal-label">Bcc</label>
            <input
              type="text"
              value={bcc}
              onChange={(e) => { setBcc(e.target.value); markDirty(); }}
              className="invite-modal-input"
              placeholder="hidden@example.com"
              data-testid="compose-email-bcc"
            />
          </>
        )}

        <label className="invite-modal-label">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => { setSubject(e.target.value); markDirty(); }}
          className="invite-modal-input"
          placeholder="(no subject)"
          data-testid="compose-email-subject"
        />

        <label className="invite-modal-label">Message</label>
        {/* Rich-text toolbar */}
        <div className="rte-toolbar" data-testid="compose-email-toolbar" data-disabled={!canSend ? "true" : "false"}>
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} title="Bold" data-testid="rte-bold"><Bold size={14} /></button>
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} title="Italic" data-testid="rte-italic"><Italic size={14} /></button>
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} title="Underline" data-testid="rte-underline"><Underline size={14} /></button>
          <span className="rte-sep" />
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} title="Bullet list" data-testid="rte-ul"><List size={14} /></button>
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} title="Numbered list" data-testid="rte-ol"><ListOrdered size={14} /></button>
          <span className="rte-sep" />
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); insertLink(); }} title="Insert link" data-testid="rte-link"><Link2 size={14} /></button>
          <button type="button" disabled={!canSend} onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }} title="Clear formatting" data-testid="rte-clear"><RemoveFormatting size={14} /></button>
        </div>
        <div
          ref={editorRef}
          className="rte-editor"
          contentEditable={canSend}
          suppressContentEditableWarning
          data-placeholder="Type your message here…"
          data-testid="compose-email-body"
          onInput={markDirty}
        />

        {/* Attachments */}
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="pg-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canSend}
            data-testid="compose-email-attach-btn"
            style={{ padding: "6px 12px", fontSize: 12 }}
          >
            <Paperclip size={13} /> Attach files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onFilePicked}
            style={{ display: "none" }}
            data-testid="compose-email-file-input"
          />
          {attachments.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }} data-testid="compose-email-attachments">
              {attachments.map((a, i) => (
                <div key={i} className="rte-attachment-row">
                  <Paperclip size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
                  <span className="rte-attachment-name" title={a.filename}>{a.filename}</span>
                  <span className="rte-attachment-size">{humanBytes(a.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    aria-label={`Remove ${a.filename}`}
                    data-testid={`compose-email-attachment-remove-${i}`}
                    className="rte-attachment-remove"
                  ><X size={11} /></button>
                </div>
              ))}
              <div className="text-[11px]" style={{ color: "var(--pg-text-mute)" }}>
                Total: {humanBytes(totalAttachmentSize)} / 20 MB
              </div>
            </div>
          )}
        </div>

        {signature ? (
          <div style={{
            background: "var(--pg-surface-soft, rgba(0,0,0,0.02))",
            border: "1px solid var(--pg-border)",
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 12,
            fontSize: 12,
            color: "var(--pg-text-mute)",
          }} data-testid="compose-email-signature-preview">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontWeight: 500, color: "var(--pg-text)" }}>Your signature</span>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={includeSignature}
                  onChange={(e) => { setIncludeSignature(e.target.checked); markDirty(); }}
                  data-testid="compose-email-include-signature"
                />
                <span>Include</span>
              </label>
            </div>
            <div
              style={{ color: "var(--pg-text-dim)", whiteSpace: /<\w+/.test(signature) ? "normal" : "pre-wrap" }}
              dangerouslySetInnerHTML={{
                __html: "--<br>" + DOMPurify.sanitize(
                  /<\w+/.test(signature) ? signature : signature.replace(/\n/g, "<br>"),
                  SIGNATURE_SANITIZE_CONFIG
                ),
              }}
            />
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--pg-text-mute)", marginTop: 10 }}>
            Tip: set up your signature in <strong>Settings → Integrations → Gmail settings</strong> and it&apos;ll be appended to every email you send.
          </div>
        )}

        <div style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          fontSize: 12,
          color: "var(--pg-text-mute)",
        }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={trackEmail}
              onChange={(e) => { setTrackEmail(e.target.checked); markDirty(); }}
              data-testid="compose-email-track"
            />
            <span>Track opens and link clicks</span>
          </label>
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            Adds an invisible pixel and rewrites links so we can log when the recipient opens or clicks.
          </span>
        </div>

        <div className="invite-modal-actions" style={{ justifyContent: "space-between" }}>
          <div>
            {draftId && (
              <button
                type="button"
                className="pg-btn"
                onClick={deleteDraft}
                data-testid="compose-email-discard-draft"
                style={{ color: "var(--pg-red)" }}
              >
                <Trash2 size={13} /> Discard draft
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="invite-modal-cancel" onClick={handleClose} data-testid="compose-email-cancel">
              Cancel
            </button>
            <button
              type="button"
              className="invite-modal-submit"
              onClick={send}
              disabled={sending || !canSend}
              data-testid="compose-email-send"
            >
              {sending ? "Sending…" : "Send email"}
              <Send strokeWidth={2.2} />
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
